import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  FormCheckbox,
  FormRadioGroup,
  InPageAlert,
  FormGroup,
  Input,
  FormInput,
} from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';
import styles from '../PlantItemDetails.module.scss';
import { radioOptionsYesNo } from 'shared/constants/constants';
import { RefData } from 'plantItemNew/constants/refData';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';

export const LiftDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;
  const {
    driveSuspensionType: savedDriveSuspensionType = '',
    otherDriveSuspensionType: savedOtherDriveSuspensionType = '',
    isMRLLift: savedIsMRLLift = 'no',
    carriageType: savedCarriageType = '',
    controlType: savedControlType = '',
    maxSpeed: savedMaxSpeed = '',
    operatingParams: savedOperatingParams = '',
    maxRatedLoad: savedMaxRatedLoad = '',
    maxPassengers: savedMaxPassengers = '',
    maxTravel: savedMaxTravel = '',
    forConstruction: savedForConstruction = '',
    inUncompliedCondition: savedInUncompliedCondition = '',
    additionalControlMeasure: savedAdditionalControlMeasure = '',
    hasBeenInspected: savedHasBeenInspected = '',
  } = value;

  const [driveSuspensionType, setDriveSuspensionType] = useState(savedDriveSuspensionType);
  const [otherDriveSuspensionType, setOtherDriveSuspensionType] = useState(savedOtherDriveSuspensionType);
  const [isMRLLift, setIsMRLLift] = useState(savedIsMRLLift);
  const [carriageType, setCarriageType] = useState(savedCarriageType);
  const [controlType, setControlType] = useState(savedControlType);
  const [maxSpeed, setMaxSpeed] = useState(savedMaxSpeed);
  const [operatingParams, setOperatingParams] = useState(savedOperatingParams);
  const [maxRatedLoad, setMaxRatedLoad] = useState(savedMaxRatedLoad);
  const [maxPassengers, setMaxPassengers] = useState(savedMaxPassengers);
  const [maxTravel, setMaxTravel] = useState(savedMaxTravel);
  const [forConstruction, setForConstruction] = useState(savedForConstruction);
  const [inUncompliedCondition, setInUncompliedCondition] = useState(savedInUncompliedCondition);
  const [additionalControlMeasure, setAdditionalControlMeasure] = useState(savedAdditionalControlMeasure);
  const [hasBeenInspected, setHasBeenInspected] = useState(savedHasBeenInspected);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const suspensionTypeOptions = Array.isArray(refData[RefData.lift.suspensionTypeOptions])
    ? refData[RefData.lift.suspensionTypeOptions].map((e) => ({
        value: e.value,
        text: e.description,
      }))
    : [];

  const carriageTypeOptions = Array.isArray(refData[RefData.lift.carriageTypeOptions])
    ? refData[RefData.lift.carriageTypeOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  const controlTypeOptions = Array.isArray(refData[RefData.lift.controlTypeOptions])
    ? refData[RefData.lift.controlTypeOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  const operatingParamOptions = [
    { value: 'maxRatedLoad', label: 'Rated load' },
    { value: 'maxPassengers', label: 'Maximum number of passengers' },
    { value: 'both', label: 'Both' },
  ];

  const validators = [
    {
      fieldName: 'driveSuspensionType',
      fieldValue: driveSuspensionType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'driveSuspensionType_other',
      fieldValue: otherDriveSuspensionType,
      isValid: (v) => !isEmpty(v) || driveSuspensionType !== 'Other (please describe)',
      errorMessage: () => 'Please enter details',
    },

    {
      fieldName: 'carriageType',
      fieldValue: carriageType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'controlType',
      fieldValue: controlType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'maxSpeed',
      fieldValue: maxSpeed,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'operatingParams',
      fieldValue: operatingParams,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'maxRatedLoad',
      fieldValue: maxRatedLoad,
      isValid: (v) => isNumeric(v) || operatingParams === 'maxPassengers',
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxPassengers',
      fieldValue: maxPassengers,
      isValid: (v) => isNumeric(v) || operatingParams === 'maxRatedLoad',
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxTravel',
      fieldValue: maxTravel,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'forConstruction',
      fieldValue: forConstruction,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'inUncompliedCondition',
      fieldValue: inUncompliedCondition,
      isValid: (v) => !isEmpty(v) || isEmpty(forConstruction) || forConstruction === 'no',
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'additionalControlMeasure',
      fieldValue: additionalControlMeasure,
      isValid: (v) =>
        !isEmpty(v) || isEmpty(forConstruction) || forConstruction === 'no' || inUncompliedCondition === 'no',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'hasBeenInspected',
      fieldValue: hasBeenInspected,
      isValid: () =>
        hasBeenInspected === 'yes' ||
        isEmpty(forConstruction) ||
        forConstruction === 'no' ||
        inUncompliedCondition === 'no',
      errorMessage: (v) =>
        isEmpty(v)
          ? 'Please select an option'
          : 'The lift & control measures must be inspected by a competent person and deemed safe to operate',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      driveSuspensionType,
      otherDriveSuspensionType,
      isMRLLift,
      carriageType,
      controlType,
      maxSpeed,
      operatingParams,
      maxRatedLoad,
      maxPassengers,
      maxTravel,
      forConstruction,
      inUncompliedCondition,
      additionalControlMeasure,
      hasBeenInspected,
    };
    onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    driveSuspensionType,
    otherDriveSuspensionType,
    isMRLLift,
    carriageType,
    controlType,
    maxSpeed,
    operatingParams,
    maxRatedLoad,
    maxPassengers,
    maxTravel,
    forConstruction,
    inUncompliedCondition,
    additionalControlMeasure,
    hasBeenInspected,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <Row>
        <Col span={9}>
          <RadioOrDropdownSelector
            id="driveSuspensionType"
            options={suspensionTypeOptions}
            title={PlantItemDetailsPage.relatedDetails.lift.driveSuspensionTypeLabel}
            errorMessage={errors?.driveSuspensionType?.errorMessage}
            hasError={showErrors && errors?.driveSuspensionType?.invalid}
            onChange={(v) => {
              setDriveSuspensionType(v);
              setOtherDriveSuspensionType('');
            }}
            value={driveSuspensionType}
            showExtraInput={driveSuspensionType === 'Other (please describe)'}
            onChangeExtraInput={setOtherDriveSuspensionType}
            extraInputValue={otherDriveSuspensionType}
            extraInputErrorMessage={errors?.driveSuspensionType_other?.errorMessage}
            extraInputHasError={showErrors && errors?.driveSuspensionType_other?.invalid}
            extraInputTitle="Other please describe"
            margin={{ top: 'md' }}
            inputWidth="lg"
          />

          <div className={`${styles.labelMsg} ${styles.marginTopOneHalf}`}>
            {PlantItemDetailsPage.relatedDetails.lift.isMRLLiftLabel}
          </div>
          <div className={styles.helpMsg}>
            Machine room less lifts are a type of either traction or hydraulic lift which do not require a machine room
            for the lift.
          </div>
          <FormCheckbox
            label="Yes, it is a Machine-Room-Less (MRL) Lift"
            onChange={() => {
              setIsMRLLift(isMRLLift === 'yes' ? 'no' : 'yes');
            }}
            checked={isMRLLift === 'yes'}
            name="mrlLift"
            value="true"
          />

          <FormRadioGroup
            id="carriageType"
            name="carriageType"
            vertical={true}
            options={carriageTypeOptions}
            onChange={setCarriageType}
            value={carriageType}
            errorMessage={errors?.carriageType?.errorMessage}
            hasError={showErrors && errors?.carriageType?.invalid}
            legend={PlantItemDetailsPage.relatedDetails.lift.carriageTypeLabel}
            margin={{ top: 'xxl' }}
          />

          <FormRadioGroup
            id="controlType"
            name="controlType"
            vertical={true}
            options={controlTypeOptions}
            onChange={setControlType}
            value={controlType}
            errorMessage={errors?.controlType?.errorMessage}
            hasError={showErrors && errors?.controlType?.invalid}
            legend={PlantItemDetailsPage.relatedDetails.lift.controlTypeLabel}
            margin={{ top: 'xxl' }}
          />
        </Col>
      </Row>
      <Row>
        <Col span={3}>
          <FormInput
            id="maxSpeed"
            label={PlantItemDetailsPage.relatedDetails.lift.maxSpeedLabel}
            suffix="m/s"
            margin={{ top: 'xxl' }}
            onChange={(e) => {
              setMaxSpeed(e.target.value);
            }}
            value={maxSpeed}
            errorMessage={errors?.maxSpeed?.errorMessage}
            hasError={showErrors && errors?.maxSpeed?.invalid}
            className={styles.inputWithSuffix}
          />
        </Col>
      </Row>
      <Row>
        <Col span={9}>
          <FormRadioGroup
            id="operatingParams"
            name="operatingParams"
            vertical={true}
            options={operatingParamOptions}
            onChange={setOperatingParams}
            value={operatingParams}
            errorMessage={errors?.operatingParams?.errorMessage}
            hasError={showErrors && errors?.operatingParams?.invalid}
            legend={PlantItemDetailsPage.relatedDetails.lift.operatingParamsLabel}
            margin={{ top: 'md' }}
          />
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          {(operatingParams === 'maxRatedLoad' || operatingParams === 'both') && (
            <FormInput
              id="maxRatedLoad"
              label={PlantItemDetailsPage.relatedDetails.lift.maxRatedLoadLabel}
              suffix="Kg"
              margin={{ top: 'lg' }}
              onChange={(e) => {
                setMaxRatedLoad(e.target.value);
              }}
              value={maxRatedLoad}
              errorMessage={errors?.maxRatedLoad?.errorMessage}
              hasError={showErrors && errors?.maxRatedLoad?.invalid}
              className={styles.inputWithSuffix}
            />
          )}

          {(operatingParams === 'maxPassengers' || operatingParams === 'both') && (
            <FormGroup
              id="maxPassengers"
              label={PlantItemDetailsPage.relatedDetails.lift.maxPassengersLabel}
              isRequired
              errorMessage={errors?.maxPassengers?.errorMessage}
              hasError={showErrors && errors?.maxPassengers?.invalid}
              margin={{ top: 'lg' }}
            >
              <Input
                onChange={(e) => {
                  setMaxPassengers(e.target.value);
                }}
                value={maxPassengers}
                inputWidth="sm"
              />
            </FormGroup>
          )}

          <FormInput
            id="maxTravel"
            label={PlantItemDetailsPage.relatedDetails.lift.maxTravelLabel}
            suffix="m"
            onChange={(e) => {
              setMaxTravel(e.target.value);
            }}
            value={maxTravel}
            errorMessage={errors?.maxTravel?.errorMessage}
            hasError={showErrors && errors?.maxTravel?.invalid}
            margin={{ top: 'lg' }}
            className={styles.inputWithSuffix}
          />
        </Col>
      </Row>
      <Row>
        <Col span={9}>
          <FormRadioGroup
            id="forConstruction"
            name="forConstruction"
            vertical={true}
            options={radioOptionsYesNo}
            onChange={(v) => {
              setForConstruction(v);
              if (v === 'no') {
                //clear children values
                setInUncompliedCondition('');
                setAdditionalControlMeasure('');
                setHasBeenInspected('');
              }
            }}
            value={forConstruction}
            errorMessage={errors?.forConstruction?.errorMessage}
            hasError={showErrors && errors?.forConstruction?.invalid}
            legend={PlantItemDetailsPage.relatedDetails.lift.forConstructionLabel}
            margin={{ top: 'xxl' }}
          />
          {forConstruction === 'yes' && (
            <>
              <FormRadioGroup
                id="inUncompliedCondition"
                name="inUncompliedCondition"
                vertical={true}
                options={radioOptionsYesNo}
                onChange={(value) => {
                  if (value === 'no') {
                    setAdditionalControlMeasure('');
                    setHasBeenInspected('');
                  }
                  setInUncompliedCondition(value);
                }}
                value={inUncompliedCondition}
                errorMessage={errors?.inUncompliedCondition?.errorMessage}
                hasError={showErrors && errors?.inUncompliedCondition?.invalid}
                legend={PlantItemDetailsPage.relatedDetails.lift.inUncompliedConditionLabel}
                margin={{ top: 'xxl' }}
              />

              {inUncompliedCondition == 'yes' && (
                <>
                  <InPageAlert
                    variant="info"
                    title="If the Lift is operated in conditions which do not comply with its registered design, additional control measures must be implemented to manage the risks to workers and others. "
                    headingLevel="h4"
                    className={styles.marginTopOneHalf}
                  />

                  <FormGroup
                    id="additionalControlMeasure"
                    label={PlantItemDetailsPage.relatedDetails.lift.additionalControlMeasureLabel}
                    isRequired
                    errorMessage={errors?.additionalControlMeasure?.errorMessage}
                    hasError={showErrors && errors?.additionalControlMeasure?.invalid}
                  >
                    <Input
                      onChange={(e) => {
                        setAdditionalControlMeasure(e.target.value);
                      }}
                      value={additionalControlMeasure}
                    />
                  </FormGroup>

                  <FormRadioGroup
                    id="hasBeenInspected"
                    name="hasbeenInspected"
                    vertical={true}
                    options={radioOptionsYesNo}
                    onChange={setHasBeenInspected}
                    value={hasBeenInspected}
                    errorMessage={errors?.hasBeenInspected?.errorMessage}
                    hasError={showErrors && errors?.hasBeenInspected?.invalid}
                    legend={PlantItemDetailsPage.relatedDetails.lift.hasBeenInspectedLabel}
                    margin={{ top: 'lg' }}
                  />

                  <InPageAlert
                    variant="info"
                    title="When the temporary control measures are altered or removed, SafeWork NSW must be notified."
                    headingLevel="h4"
                    className={styles.marginTopOneHalf}
                  />
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </>
  );
});

LiftDetails.displayName = 'LiftDetails';
